import React, {useCallback, useMemo, useRef, useState,useEffect} from 'react'
import './ModalUserEdit.css'
import { getCurrentUser } from '../utils/auth'
import { useNavigate } from 'react-router-dom'
import { BsFillPencilFill, BsFillTrashFill, BsPlusLg } from 'react-icons/bs'

const normalizeTypeDescription = (value) => String(value ?? '').trim().toLowerCase();
const isAllowedScopedType = (description) => {
    const normalized = normalizeTypeDescription(description);
    return normalized === 'employee' || normalized === 'customer admin';
};
const isAllowedGlobalType = (description) => {
    const normalized = normalizeTypeDescription(description);
    return normalized === 'employee' || normalized === 'customer admin' || normalized === 'corporate admin';
};

const PHONE_TYPE_OPTIONS = [
    { id: 1, label: 'Mobile' },
    { id: 2, label: 'Home' },
    { id: 3, label: 'Work' },
    { id: 4, label: 'Facility' },
    { id: 5, label: 'Corporate' },
];

const STATE_OPTIONS = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
    'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
    'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

const REQUEST_HEADERS = {
    'machine':'2',
    'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
};

const USER_KEYS_TO_REMOVE_ON_SAVE = ['user_type_id', 'created_on'];

const sortObjectDeep = (value) => {
    if (Array.isArray(value)) {
        return value.map(sortObjectDeep);
    }

    if (value && typeof value === 'object') {
        return Object.keys(value)
            .sort()
            .reduce((accumulator, key) => {
                accumulator[key] = sortObjectDeep(value[key]);
                return accumulator;
            }, {});
    }

    return value;
};

const normalizeUserForSave = (user) => {
    const normalizedUser = {...(user || {})};
    USER_KEYS_TO_REMOVE_ON_SAVE.forEach((key) => {
        delete normalizedUser[key];
    });
    return normalizedUser;
};

const buildSaveSnapshot = (user) => JSON.stringify({
    user: sortObjectDeep(normalizeUserForSave(user)),
});

const ModalUserEdit = ({closeModal, onSubmit, userEdit, allUserTypes, allCustomers, isReadOnly = false}) => {
    const tokenPayload = getCurrentUser() || {};
    const tokenUserType = Number(tokenPayload?.user_type);
    const userScopes = tokenPayload?.scopes || [];
    const navigate = useNavigate();
    const isReadOnlyMode = !!isReadOnly;
    const [userInfo, setUserInfo] = useState(userEdit);
    const [customerSelectDisabled, setCustomerSelectDisabled] = useState(false);
    const [phones, setPhones] = useState([]);
    const [addresses, setAddresses] = useState([]);
    const [addressTypes, setAddressTypes] = useState([]);
    const [isLoadingModalData, setIsLoadingModalData] = useState(true);
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [showPhoneRemoveConfirm, setShowPhoneRemoveConfirm] = useState(false);
    const [phonePendingDelete, setPhonePendingDelete] = useState(null);
    const [showPhoneEditor, setShowPhoneEditor] = useState(false);
    const [showAddressRemoveConfirm, setShowAddressRemoveConfirm] = useState(false);
    const [addressPendingDelete, setAddressPendingDelete] = useState(null);
    const [showAddressEditor, setShowAddressEditor] = useState(false);
    const [phoneEditorState, setPhoneEditorState] = useState({
        mode: 'add',
        userPhoneId: null,
        phoneTypeId: 1,
        phone: '',
    });
    const [addressEditorState, setAddressEditorState] = useState({
        mode: 'add',
        userAddressId: null,
        addressTypeId: 1,
        street1: '',
        street2: '',
        city: '',
        state: '',
        zip: '',
    });
    const [errors, setErrors] = useState("");
    const [initialSaveSnapshot, setInitialSaveSnapshot] = useState('');
    const hasCapturedInitialSnapshotRef = useRef(false);

    const hasUpdateGlobalUser = userScopes.includes('update:userData');
    const hasUpdateScopedUser = userScopes.some((scope) => scope.startsWith('update:userData') && scope !== 'update:userData');

    const currentUserTypeDescription = useMemo(() => {
        const matched = (allUserTypes || []).find((typeOption) => Number(typeOption.id) === tokenUserType);
        return matched?.description || tokenPayload?.user_type_description || tokenPayload?.description || '';
    }, [allUserTypes, tokenUserType, tokenPayload?.user_type_description, tokenPayload?.description]);

    const currentUserIsCreator = normalizeTypeDescription(currentUserTypeDescription) === 'creator';

    const filteredUserTypeOptions = useMemo(() => {
        const options = Array.isArray(allUserTypes) ? allUserTypes : [];

        if (currentUserIsCreator) {
            return options;
        }

        if (hasUpdateGlobalUser) {
            return options.filter((option) => isAllowedGlobalType(option.description));
        }

        if (hasUpdateScopedUser) {
            return options.filter((option) => isAllowedScopedType(option.description));
        }

        return options;
    }, [allUserTypes, currentUserIsCreator, hasUpdateGlobalUser, hasUpdateScopedUser]);

    const userTypeOptionsForSelect = isReadOnlyMode
        ? (Array.isArray(allUserTypes) ? allUserTypes : [])
        : filteredUserTypeOptions;

    const fetchUserPhones = useCallback(async (userId) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/user_phones/user/' + userId,
            {
                method: 'GET',
                headers: REQUEST_HEADERS,
            });

        if (!response.ok) {
            throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
        }

        const data = await response.json();
        setPhones(Array.isArray(data) ? data : []);
    }, []);

    const fetchUserAddresses = useCallback(async (userId) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/user_addresses/user/' + userId,
            {
                method: 'GET',
                headers: REQUEST_HEADERS,
            });

        if (!response.ok) {
            throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
        }

        const data = await response.json();
        setAddresses(Array.isArray(data) ? data : []);
    }, []);

    const fetchAddressTypes = useCallback(async () => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/address_types/',
            {
                method: 'GET',
                headers: REQUEST_HEADERS,
            });

        if (!response.ok) {
            throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
        }

        const data = await response.json();
        setAddressTypes(Array.isArray(data) ? data : []);
    }, []);

    const apiCreatePhone = async (payload) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/user_phones/',
            {
                method: 'POST',
                headers: {
                    ...REQUEST_HEADERS,
                    'content-type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

        if (!response.ok) {
            throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
        }

        return response.json();
    };

    const apiUpdatePhone = async (userPhoneId, payload) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/user_phones/user_phone/' + userPhoneId,
            {
                method: 'PATCH',
                headers: {
                    ...REQUEST_HEADERS,
                    'content-type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

        if (!response.ok) {
            throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
        }

        return response.json();
    };

    const apiDeletePhone = async (userPhoneId) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/user_phones/user_phone/' + userPhoneId,
            {
                method: 'DELETE',
                headers: REQUEST_HEADERS,
            });

        if (!response.ok) {
            throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
        }
    };

    const apiCreateAddress = async (payload) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/user_addresses/',
            {
                method: 'POST',
                headers: {
                    ...REQUEST_HEADERS,
                    'content-type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

        if (!response.ok) {
            throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
        }

        return response.json();
    };

    const apiUpdateAddress = async (userAddressId, payload) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/user_addresses/user_address/' + userAddressId,
            {
                method: 'PATCH',
                headers: {
                    ...REQUEST_HEADERS,
                    'content-type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

        if (!response.ok) {
            throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
        }

        return response.json();
    };

    const apiDeleteAddress = async (userAddressId) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/user_addresses/user_address/' + userAddressId,
            {
                method: 'DELETE',
                headers: REQUEST_HEADERS,
            });

        if (!response.ok) {
            throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
        }
    };

    useEffect(() => {
        if (isReadOnlyMode) {
            return;
        }

        if (!Array.isArray(filteredUserTypeOptions) || filteredUserTypeOptions.length === 0) {
            return;
        }

        const currentTypeId = Number(userInfo?.user_type_id ?? userInfo?.user_type);
        const isCurrentAllowed = filteredUserTypeOptions.some((option) => Number(option.id) === currentTypeId);
        if (isCurrentAllowed) {
            return;
        }

        const fallbackType = filteredUserTypeOptions[0];
        if (!fallbackType) {
            return;
        }

        setUserInfo((previous) => ({
            ...previous,
            description: fallbackType.description,
            user_type_id: Number(fallbackType.id),
            user_type: Number(fallbackType.id),
        }));
    }, [filteredUserTypeOptions, isReadOnlyMode, userInfo?.user_type, userInfo?.user_type_id]);

    useEffect(() => {
        let isMounted = true;

        const loadModalData = async () => {
            setIsLoadingModalData(true);

            try {
                if (tokenUserType < 3) {
                    setCustomerSelectDisabled(true);
                }

                await Promise.all([
                    fetchUserAddresses(userInfo.id),
                    fetchUserPhones(userInfo.id),
                    fetchAddressTypes(),
                ]);

                if (!isMounted) {
                    return;
                }
            }
            catch (error) {
                console.error(error);
            }
            finally {
                if (isMounted) {
                    setIsLoadingModalData(false);
                }
            }
        };

        loadModalData();

        return () => {
            isMounted = false;
        };
    }, [fetchAddressTypes, fetchUserAddresses, fetchUserPhones, userInfo.id, tokenUserType]);

    useEffect(() => {
        if (isLoadingModalData || hasCapturedInitialSnapshotRef.current) {
            return;
        }

        setInitialSaveSnapshot(buildSaveSnapshot(userInfo));
        hasCapturedInitialSnapshotRef.current = true;
    }, [isLoadingModalData, userInfo]);

    const apiPatchUserInfo = async (userId, patchUserInfo) => {
        try{
            const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/users/user/' + userId,
                {
                    method: 'PATCH',
                    headers: {
                        'machine':'2',
                        'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                        'content-type': 'application/json'
                    },
                    body: JSON.stringify(patchUserInfo),
                });
            if (!response.ok) {
                throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
            }
            await response.json();
        } catch (error) {
            console.error(error);
        }
    }

    const extractPhoneDigits = (value) => String(value ?? '').replace(/\D/g, '').slice(0, 10);

    const formatPhoneInput = (value) => {
        const digits = extractPhoneDigits(value);

        if (digits.length === 0) {
            return '';
        }

        if (digits.length <= 3) {
            return `(${digits}`;
        }

        if (digits.length <= 6) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        }

        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    };

    function formatPhoneNumber(phone) {
        return formatPhoneInput(phone);
    }

    const getPhoneTypeId = (phone) => {
        if (!phone) {
            return 1;
        }

        const nestedPhoneType = (typeof phone.phone_type === 'object' && phone.phone_type !== null)
            ? phone.phone_type
            : null;

        const idCandidates = [
            phone.phone_type_id,
            typeof phone.phone_type === 'number' ? phone.phone_type : null,
            nestedPhoneType?.phone_type_id,
            nestedPhoneType?.id,
            nestedPhoneType?.type_id,
        ];

        for (const candidate of idCandidates) {
            const numericId = Number(candidate);
            if (Number.isInteger(numericId) && PHONE_TYPE_OPTIONS.some((option) => option.id === numericId)) {
                return numericId;
            }
        }

        const normalizeLabel = (value) => String(value ?? '')
            .trim()
            .toLowerCase()
            .replace(/[^a-z]/g, '');

        const labelCandidates = [
            phone.phone_type,
            phone.phone_type_description,
            phone.phone_type_name,
            nestedPhoneType?.phone_type,
            nestedPhoneType?.description,
            nestedPhoneType?.label,
            nestedPhoneType?.name,
        ];

        for (const candidate of labelCandidates) {
            const normalizedCandidate = normalizeLabel(candidate);
            if (!normalizedCandidate) {
                continue;
            }

            const matchedOption = PHONE_TYPE_OPTIONS.find((option) => {
                const normalizedOption = normalizeLabel(option.label);
                return normalizedCandidate === normalizedOption
                    || normalizedCandidate.includes(normalizedOption)
                    || normalizedOption.includes(normalizedCandidate);
            });

            if (matchedOption) {
                return matchedOption.id;
            }
        }

        return 1;
    };

    const openAddPhoneModal = () => {
        setPhoneEditorState({
            mode: 'add',
            userPhoneId: null,
            phoneTypeId: 1,
            phone: '',
        });
        setShowPhoneEditor(true);
    };

    const openEditPhoneModal = (phone) => {
        setPhoneEditorState({
            mode: 'edit',
            userPhoneId: Number(phone?.user_phone_id),
            phoneTypeId: getPhoneTypeId(phone),
            phone: formatPhoneInput(phone?.phone ?? phone?.phone_number),
        });
        setShowPhoneEditor(true);
    };

    const handlePhoneEditorCancel = () => {
        setShowPhoneEditor(false);
        setPhoneEditorState({
            mode: 'add',
            userPhoneId: null,
            phoneTypeId: 1,
            phone: '',
        });
    };

    const handlePhoneEditorPhoneChange = (event) => {
        const formatted = formatPhoneInput(event.target.value);
        setPhoneEditorState((previous) => ({
            ...previous,
            phone: formatted,
        }));
    };

    const handlePhoneEditorTypeChange = (event) => {
        setPhoneEditorState((previous) => ({
            ...previous,
            phoneTypeId: Number(event.target.value),
        }));
    };

    const handlePhoneEditorSave = async () => {
        const digits = extractPhoneDigits(phoneEditorState.phone);
        if (!userInfo?.id || digits.length !== 10) {
            return;
        }

        const payload = {
            web_user: Number(userInfo.id),
            phone_type: Number(phoneEditorState.phoneTypeId),
            phone: digits,
        };

        try {
            if (phoneEditorState.mode === 'add') {
                await apiCreatePhone(payload);
            } else if (phoneEditorState.userPhoneId) {
                await apiUpdatePhone(phoneEditorState.userPhoneId, payload);
            }

            await fetchUserPhones(userInfo.id);
            handlePhoneEditorCancel();
        } catch (error) {
            console.error(error);
        }
    };

    const openRemovePhoneDialog = (phone) => {
        setPhonePendingDelete(phone);
        setShowPhoneRemoveConfirm(true);
    };

    const handleCancelRemovePhoneDialog = () => {
        setShowPhoneRemoveConfirm(false);
        setPhonePendingDelete(null);
    };

    const handleConfirmRemovePhone = async () => {
        const phoneId = Number(phonePendingDelete?.user_phone_id);
        if (!userInfo?.id || !phoneId) {
            handleCancelRemovePhoneDialog();
            return;
        }

        try {
            await apiDeletePhone(phoneId);
            await fetchUserPhones(userInfo.id);
        } catch (error) {
            console.error(error);
        } finally {
            handleCancelRemovePhoneDialog();
        }
    };

    function getPhoneTypeLabel(phone) {
        if (!phone) return '';

        if (typeof phone.phone_type === 'object' && phone.phone_type !== null) {
            return phone.phone_type.phone_type || phone.phone_type.description || '';
        }

        if (typeof phone.phone_type === 'string') {
            return phone.phone_type;
        }

        const typeId = phone.phone_type_id || phone.phone_type;
        const phoneTypeMap = {
            1: 'Mobile',
            2: 'Home',
            3: 'Work',
            4: 'Facility',
            5: 'Corporate',
        };

        return phoneTypeMap[typeId] || '';
    }

    function getAddressTypeLabel(address) {
        if (!address) return '';

        const resolveFromLoadedTypes = (typeIdValue) => {
            const typeId = Number(typeIdValue);
            if (!Number.isInteger(typeId)) {
                return '';
            }

            const matched = addressTypes.find((addressType) => {
                const candidateId = Number(addressType?.address_type_id ?? addressType?.id);
                return candidateId === typeId;
            });

            return matched?.address_type || matched?.description || matched?.name || '';
        };

        if (typeof address.address_type === 'object' && address.address_type !== null) {
            return address.address_type.address_type || address.address_type.description || '';
        }

        if (typeof address.address_type === 'string') {
            return address.address_type;
        }

        const typeId = address.address_type_id || address.address_type;
        const loadedLabel = resolveFromLoadedTypes(typeId);
        if (loadedLabel) {
            return loadedLabel;
        }

        const addressTypeMap = {
            1: 'Home',
            2: 'Work',
            3: 'Shipping',
            4: 'Billing',
            5: 'Facility',
            6: 'Corporate Office',
        };

        return addressTypeMap[typeId] || 'Address';
    }

    const addressTypeOptions = useMemo(() => {
        if (Array.isArray(addressTypes) && addressTypes.length > 0) {
            return addressTypes
                .map((addressType) => ({
                    id: Number(addressType?.address_type_id ?? addressType?.id),
                    label: String(addressType?.address_type ?? addressType?.description ?? addressType?.name ?? '').trim(),
                }))
                .filter((addressType) => Number.isInteger(addressType.id) && addressType.label);
        }

        return [
            { id: 1, label: 'Home' },
            { id: 2, label: 'Work' },
            { id: 3, label: 'Shipping' },
            { id: 4, label: 'Billing' },
            { id: 5, label: 'Facility' },
            { id: 6, label: 'Corporate Office' },
        ];
    }, [addressTypes]);

    const getAddressTypeId = (address) => {
        if (!address) {
            return 1;
        }

        if (address.address_type_id) {
            return Number(address.address_type_id);
        }

        if (typeof address.address_type === 'number') {
            return Number(address.address_type);
        }

        if (typeof address.address_type === 'object' && address.address_type !== null) {
            return Number(address.address_type.address_type_id ?? address.address_type.id ?? 1);
        }

        return 1;
    };

    const openAddAddressModal = () => {
        setAddressEditorState({
            mode: 'add',
            userAddressId: null,
            addressTypeId: 1,
            street1: '',
            street2: '',
            city: '',
            state: '',
            zip: '',
        });
        setShowAddressEditor(true);
    };

    const openEditAddressModal = (address) => {
        setAddressEditorState({
            mode: 'edit',
            userAddressId: Number(address?.user_address_id),
            addressTypeId: getAddressTypeId(address),
            street1: String(address?.street_1 ?? ''),
            street2: String(address?.street_2 ?? ''),
            city: String(address?.city ?? ''),
            state: String(address?.st ?? '').toUpperCase(),
            zip: String(address?.zip ?? ''),
        });
        setShowAddressEditor(true);
    };

    const handleAddressEditorCancel = () => {
        setShowAddressEditor(false);
        setAddressEditorState({
            mode: 'add',
            userAddressId: null,
            addressTypeId: 1,
            street1: '',
            street2: '',
            city: '',
            state: '',
            zip: '',
        });
    };

    const handleAddressEditorChange = (field, value) => {
        setAddressEditorState((previous) => ({
            ...previous,
            [field]: field === 'state' ? String(value).toUpperCase() : value,
        }));
    };

    const isAddressEditorValid = () => {
        return String(addressEditorState.street1).trim() !== ''
            && String(addressEditorState.city).trim() !== ''
            && String(addressEditorState.state).trim() !== ''
            && String(addressEditorState.zip).trim() !== '';
    };

    const handleAddressEditorSave = async () => {
        if (!userInfo?.id || !isAddressEditorValid()) {
            return;
        }

        const payload = {
            web_user: Number(userInfo.id),
            address_type: Number(addressEditorState.addressTypeId),
            street_1: String(addressEditorState.street1 ?? '').trim(),
            street_2: String(addressEditorState.street2 ?? '').trim(),
            city: String(addressEditorState.city ?? '').trim(),
            st: String(addressEditorState.state ?? '').toUpperCase(),
            zip: String(addressEditorState.zip ?? '').trim(),
        };

        try {
            if (addressEditorState.mode === 'add') {
                await apiCreateAddress(payload);
            } else if (addressEditorState.userAddressId) {
                await apiUpdateAddress(addressEditorState.userAddressId, payload);
            }

            await fetchUserAddresses(userInfo.id);
            handleAddressEditorCancel();
        } catch (error) {
            console.error(error);
        }
    };

    const openRemoveAddressDialog = (address) => {
        setAddressPendingDelete(address);
        setShowAddressRemoveConfirm(true);
    };

    const handleCancelRemoveAddressDialog = () => {
        setShowAddressRemoveConfirm(false);
        setAddressPendingDelete(null);
    };

    const handleConfirmRemoveAddress = async () => {
        const addressId = Number(addressPendingDelete?.user_address_id);
        if (!userInfo?.id || !addressId) {
            handleCancelRemoveAddressDialog();
            return;
        }

        try {
            await apiDeleteAddress(addressId);
            await fetchUserAddresses(userInfo.id);
        } catch (error) {
            console.error(error);
        } finally {
            handleCancelRemoveAddressDialog();
        }
    };

    const formatAddressPreview = (address) => {
        const lineOne = String(address?.street_1 ?? '').trim();
        const lineTwo = String(address?.street_2 ?? '').trim();
        const city = String(address?.city ?? '').trim();
        const state = String(address?.st ?? '').trim();
        const zip = String(address?.zip ?? '').trim();

        return [
            [lineOne, lineTwo].filter(Boolean).join(' '),
            [city, state, zip].filter(Boolean).join(', ').replace(', ,', ', '),
        ].filter(Boolean);
    };

    const validateForm = async () => {
        if (userInfo.first_name && userInfo.last_name && userInfo.username && userInfo.email) {
            setErrors("")
            const patchUserInfo = normalizeUserForSave(userInfo)
            const userId = patchUserInfo.id
            await apiPatchUserInfo(userId, patchUserInfo)
            return true;
        }
        else {
            let errorFields = [];
            for(const [key, value] of Object.entries(userInfo)) {
                if(!value){
                    errorFields.push(key);
                }
            }
            setErrors(errorFields.join(", "));
            return false;
        }
    }

    const handleChange = (e) => {
        setUserInfo({
            ...userInfo,
            [e.target.name]: e.target.value
        })
    }

    const handleTypeChange = (e) => {
        setUserInfo({
            ...userInfo,
            'description': allUserTypes[e.target.selectedIndex].description,
            'user_type_id': allUserTypes[e.target.selectedIndex].id,
            'user_type': allUserTypes[e.target.selectedIndex].id
        })
        //console.log(allUserTypes)
    }

    const handleCustomerChange = (e) => {
        const selectedCustomerId = Number(e.target.value);
        const selectedCustomer = (allCustomers ?? []).find((customer) => {
            const customerId = customer.id ?? customer.customer_id;
            return Number(customerId) === selectedCustomerId;
        });

        if (!selectedCustomer) {
            return;
        }

        setUserInfo({
            ...userInfo,
            customer_name: selectedCustomer.customer_name,
            customer_id: selectedCustomerId,
            customer: selectedCustomerId,
        });
    }

    const handleCheckChange = (e) => {
        setUserInfo({
            ...userInfo,
            [e.target.name]: e.target.checked
        })
    }

    const handleSubmit = async (e) => {
        if (isReadOnlyMode) {
            return;
        }
        e.preventDefault();

        const currentSaveSnapshot = buildSaveSnapshot(userInfo);
        if (hasCapturedInitialSnapshotRef.current && initialSaveSnapshot === currentSaveSnapshot) {
            closeModal();
            return;
        }

        var isValidated = await validateForm()
        if (!isValidated) return;

        onSubmit(userInfo);
        closeModal();
    }

    const handleCancel = (e) => {
        e.preventDefault();
        closeModal();
    }

    const handleOpenResetConfirm = (e) => {
        e.preventDefault();
        setShowResetConfirm(true);
    }

    const handleCancelResetConfirm = (e) => {
        e.preventDefault();
        setShowResetConfirm(false);
    }

    const normalizeUsername = (value) => String(value ?? '')
        .normalize('NFKC')
        .replace(/[\s\u200B-\u200D\uFEFF]/g, '')
        .toLowerCase();

    const handleConfirmResetPassword = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const displayName = `${userInfo?.first_name ?? ''} ${userInfo?.last_name ?? ''}`.trim();
        const selectedUsername = normalizeUsername(userInfo?.username);
        const selectedUserId = userInfo?.id ?? userInfo?.user_id ?? userInfo?.web_user_id ?? null;

        let loggedInUsername = '';
        let loggedInUserId = null;
        const currentUser = getCurrentUser();
        if (currentUser) {
            loggedInUsername = normalizeUsername(currentUser?.username);
            loggedInUserId = currentUser?.user_id ?? currentUser?.id ?? currentUser?.web_user_id ?? null;
        }

        const isSelfByUsername = !!(loggedInUsername && selectedUsername && loggedInUsername === selectedUsername);
        const isSelfById = loggedInUserId !== null && selectedUserId !== null && String(loggedInUserId) === String(selectedUserId);
        const isSelfReset = isSelfByUsername || isSelfById;

        const destination = isSelfReset ? '/resetpassword' : `/users/${userInfo?.id}/reset-password`;
        const navigationState = isSelfReset ? undefined : {
            state: {
                userName: displayName
            }
        };

        setShowResetConfirm(false);

        navigate(destination, {
            ...(navigationState ?? {}),
            replace: true,
        });
    }

    const visiblePhones = phones.filter((phone) => {
        const value = phone?.phone ?? phone?.phone_number ?? '';
        return String(value).trim() !== '';
    });

    const visibleAddresses = addresses.filter((address) => {
        const addressValues = [
            address?.street_1,
            address?.street_2,
            address?.city,
            address?.st,
            address?.zip,
        ];

        return addressValues.some((value) => String(value ?? '').trim() !== '');
    });

    return (
    <div className='modal-container'>
      <div className='modal-edit'>
                {!isReadOnlyMode && <button className='modal-close-btn' onClick={handleCancel} aria-label='Close user details modal'>X</button>}
        <form>
            {isLoadingModalData ? (
                <div className='user-skeleton-grid'>
                    <section className='user-card'>
                        <div className='user-skeleton-line user-skeleton-title'></div>
                        <div className='user-skeleton-row'>
                            <div className='user-skeleton-line user-skeleton-input'></div>
                            <div className='user-skeleton-line user-skeleton-input'></div>
                        </div>
                        <div className='user-skeleton-row'>
                            <div className='user-skeleton-line user-skeleton-input'></div>
                            <div className='user-skeleton-line user-skeleton-input'></div>
                        </div>
                    </section>
                    <section className='user-card'>
                        <div className='user-skeleton-line user-skeleton-title short'></div>
                        <div className='user-skeleton-line user-skeleton-input'></div>
                    </section>
                    <section className='user-card'>
                        <div className='user-skeleton-line user-skeleton-title short'></div>
                        <div className='user-skeleton-line user-skeleton-input'></div>
                        <div className='user-skeleton-line user-skeleton-input'></div>
                    </section>
                </div>
            ) : (
            <div className='user-card-grid'>
                <section className='user-card'>
                    <div className='user-card-header'>
                        <h3>User Details</h3>
                        <div className='user-toggle'>
                            <input id='is_active' name="is_active" type="checkbox" value={userInfo.is_active} onChange={handleCheckChange} checked={userInfo.is_active} disabled={isReadOnlyMode}/>
                            <label htmlFor='is_active'>Active</label>
                        </div>
                    </div>
                    <div className='user-fields-grid two-col'>
                        <div className='form-group wide'>
                            <label htmlFor='user_id'>ID: {userInfo.id}</label>
                        </div>
                        <div className='form-group'>
                            <label htmlFor='first_name'>First Name*</label>
                            <input name="first_name" value={userInfo.first_name} onChange={handleChange} readOnly={isReadOnlyMode}/>
                        </div>
                        <div className='form-group'>
                            <label htmlFor='last_name'>Last Name*</label>
                            <input name="last_name" value={userInfo.last_name} onChange={handleChange} readOnly={isReadOnlyMode}/>
                        </div>
                        <div className='form-group'>
                            <label htmlFor='username'>Username*</label>
                            <input name="username" value={userInfo.username} onChange={handleChange} readOnly={isReadOnlyMode}/>
                        </div>
                        <div className='form-group'>
                            <label htmlFor='email'>Email*</label>
                            <input name="email" value={userInfo.email} onChange={handleChange} readOnly={isReadOnlyMode}/>
                        </div>
                        <div className='form-group'>
                            <label htmlFor='user_type'>Type</label>
                            <select name="description" value={userInfo.user_type_id} onChange={handleTypeChange} disabled={isReadOnlyMode}>
                                {userTypeOptionsForSelect.map ((user_type) => (
                                    <option key={user_type.id} value={user_type.id}>
                                        {user_type.description}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className='form-group'>
                            <label htmlFor='customer'>Customer</label>
                            <select
                                name="customer"
                                value={userInfo.customer_id}
                                onChange={handleCustomerChange}
                                disabled={customerSelectDisabled || isReadOnlyMode}
                                >
                                {allCustomers.map ((customer) => (
                                    <option key={customer.id} value={customer.id} customer_id={customer.id}>
                                        {customer.customer_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {!isReadOnlyMode && <div className='user-reset-button-row'>
                        <button type='button' className='user-reset-card-link' onClick={handleOpenResetConfirm}>Reset Password</button>
                    </div>}
                </section>

                <section className='user-card'>
                    <div className='contact-card-header'>
                        <h3>Contact</h3>
                        {!isReadOnlyMode && (
                            <button
                                type='button'
                                className='user-card-link-btn'
                                onClick={openAddPhoneModal}
                            >
                                <BsPlusLg aria-hidden='true' />
                                <span>Add Phone</span>
                            </button>
                        )}
                    </div>
                    <div className='phone-card-list'>
                        {visiblePhones.length > 0 ? visiblePhones.map((phone, i) => (
                            <div className='phone-card' key={phone.user_phone_id ?? i}>
                                <div className='phone-card-row'>
                                    <div className='phone-card-title'>{getPhoneTypeLabel(phone) || 'Phone'}</div>
                                    <div className='phone-card-value'>{formatPhoneNumber(phone.phone ?? phone.phone_number)}</div>
                                    {!isReadOnlyMode && (
                                        <div className='phone-card-actions'>
                                            <button
                                                type='button'
                                                className='icon-btn'
                                                aria-label='Edit phone'
                                                onClick={() => openEditPhoneModal(phone)}
                                            >
                                                <BsFillPencilFill />
                                            </button>
                                            <button
                                                type='button'
                                                className='icon-btn danger'
                                                aria-label='Remove phone'
                                                onClick={() => openRemovePhoneDialog(phone)}
                                            >
                                                <BsFillTrashFill />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )) : <div className='empty-card-spacer'></div>}
                    </div>
                </section>

                <section className='user-card address-card'>
                    <div className='contact-card-header'>
                        <h3>Addresses</h3>
                        {!isReadOnlyMode && (
                            <button
                                type='button'
                                className='user-card-link-btn'
                                onClick={openAddAddressModal}
                            >
                                <BsPlusLg aria-hidden='true' />
                                <span>Add Address</span>
                            </button>
                        )}
                    </div>
                    <div className='address-card-list'>
                        {visibleAddresses.length > 0 ? visibleAddresses.map((address, i) => (
                            <div className='address-item' key={address.user_address_id ?? i}>
                                <div className='address-item-header'>
                                    <span className='address-type-badge'>{getAddressTypeLabel(address)}</span>
                                    <div className='address-inline-value'>
                                        {formatAddressPreview(address).join(' | ')}
                                    </div>
                                    {!isReadOnlyMode && (
                                        <div className='phone-card-actions'>
                                            <button
                                                type='button'
                                                className='icon-btn'
                                                aria-label='Edit address'
                                                onClick={() => openEditAddressModal(address)}
                                            >
                                                <BsFillPencilFill />
                                            </button>
                                            <button
                                                type='button'
                                                className='icon-btn danger'
                                                aria-label='Remove address'
                                                onClick={() => openRemoveAddressDialog(address)}
                                            >
                                                <BsFillTrashFill />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )) : <div className='empty-card-spacer'></div>}
                    </div>
                </section>
            </div>
            )}

            {!isReadOnlyMode && !isLoadingModalData && errors && <div className='error'>{`Please include: ${errors}`}</div>}
            <div className='user-actions-row'>
                {isReadOnlyMode ? (
                    <button type='button' className='btn btn-cancel' onClick={handleCancel}>Close</button>
                ) : (
                    <>
                        <button type='button' className='btn btn-cancel' onClick={handleCancel}>Close</button>
                        {!isLoadingModalData && <button type='submit' className='btn' onClick={handleSubmit}>Save</button>}
                    </>
                )}
            </div>
        </form>
      </div>

      {!isReadOnlyMode && showResetConfirm && (
        <div className='modal-container' onClick={(e) => {
            if (e.target.className === 'modal-container') {
                setShowResetConfirm(false);
            }
        }}>
            <div className='modal-edit user-reset-confirm-modal'>
                <p className='user-reset-confirm-message'>
                    Are you sure you want to reset the password for {userInfo?.first_name} {userInfo?.last_name}? (User ID: {userInfo?.id})
                </p>
                <div className='user-reset-confirm-actions'>
                    <button type='button' className='btn btn-cancel' onClick={handleCancelResetConfirm}>Cancel</button>
                    <button type='button' className='btn' onClick={handleConfirmResetPassword}>Reset Password</button>
                </div>
            </div>
        </div>
      )}

      {!isReadOnlyMode && showPhoneRemoveConfirm && (
        <div className='modal-container' onClick={(e) => {
            if (e.target.className === 'modal-container') {
                handleCancelRemovePhoneDialog();
            }
        }}>
            <div className='modal-edit user-reset-confirm-modal'>
                <p className='user-reset-confirm-message'>
                    Are you sure you want to remove {getPhoneTypeLabel(phonePendingDelete) || 'this'} phone number {formatPhoneNumber(phonePendingDelete?.phone ?? phonePendingDelete?.phone_number)}?
                </p>
                <div className='user-reset-confirm-actions'>
                    <button type='button' className='btn btn-cancel' onClick={handleCancelRemovePhoneDialog}>Cancel</button>
                    <button type='button' className='btn' onClick={handleConfirmRemovePhone}>Remove</button>
                </div>
            </div>
        </div>
      )}

      {!isReadOnlyMode && showPhoneEditor && (
        <div className='modal-container' onClick={(e) => {
            if (e.target.className === 'modal-container') {
                handlePhoneEditorCancel();
            }
        }}>
            <div className='modal-edit user-phone-editor-modal'>
                <h3 className='user-phone-editor-title'>{phoneEditorState.mode === 'add' ? 'Add Phone' : 'Edit Phone'}</h3>
                <div className='form-group'>
                    <label htmlFor='phone_type_edit'>Phone Type</label>
                    <select id='phone_type_edit' value={phoneEditorState.phoneTypeId} onChange={handlePhoneEditorTypeChange}>
                        {PHONE_TYPE_OPTIONS.map((phoneType) => (
                            <option key={phoneType.id} value={phoneType.id}>{phoneType.label}</option>
                        ))}
                    </select>
                </div>
                <div className='form-group'>
                    <label htmlFor='phone_number_edit'>Phone Number</label>
                    <input
                        id='phone_number_edit'
                        type='text'
                        value={phoneEditorState.phone}
                        onChange={handlePhoneEditorPhoneChange}
                        placeholder='(555) 555-5555'
                    />
                </div>
                <div className='user-reset-confirm-actions'>
                    <button type='button' className='btn btn-cancel' onClick={handlePhoneEditorCancel}>Cancel</button>
                    <button
                        type='button'
                        className='btn'
                        onClick={handlePhoneEditorSave}
                        disabled={extractPhoneDigits(phoneEditorState.phone).length !== 10}
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
      )}

      {!isReadOnlyMode && showAddressRemoveConfirm && (
        <div className='modal-container' onClick={(e) => {
            if (e.target.className === 'modal-container') {
                handleCancelRemoveAddressDialog();
            }
        }}>
            <div className='modal-edit user-reset-confirm-modal'>
                <p className='user-reset-confirm-message'>
                    Are you sure you want to remove this address?
                </p>
                <div className='user-reset-confirm-actions'>
                    <button type='button' className='btn btn-cancel' onClick={handleCancelRemoveAddressDialog}>Cancel</button>
                    <button type='button' className='btn' onClick={handleConfirmRemoveAddress}>Remove</button>
                </div>
            </div>
        </div>
      )}

      {!isReadOnlyMode && showAddressEditor && (
        <div className='modal-container' onClick={(e) => {
            if (e.target.className === 'modal-container') {
                handleAddressEditorCancel();
            }
        }}>
            <div className='modal-edit user-phone-editor-modal'>
                <h3 className='user-phone-editor-title'>{addressEditorState.mode === 'add' ? 'Add Address' : 'Edit Address'}</h3>
                <div className='form-group'>
                    <label htmlFor='address_type_edit'>Address Type</label>
                    <select
                        id='address_type_edit'
                        value={addressEditorState.addressTypeId}
                        onChange={(event) => handleAddressEditorChange('addressTypeId', Number(event.target.value))}
                    >
                        {addressTypeOptions.map((addressTypeOption) => (
                            <option key={addressTypeOption.id} value={addressTypeOption.id}>{addressTypeOption.label}</option>
                        ))}
                    </select>
                </div>
                <div className='form-group'>
                    <label htmlFor='address_street_1'>Street</label>
                    <input
                        id='address_street_1'
                        type='text'
                        value={addressEditorState.street1}
                        onChange={(event) => handleAddressEditorChange('street1', event.target.value)}
                    />
                </div>
                <div className='form-group'>
                    <label htmlFor='address_street_2'>Street 2</label>
                    <input
                        id='address_street_2'
                        type='text'
                        value={addressEditorState.street2}
                        onChange={(event) => handleAddressEditorChange('street2', event.target.value)}
                    />
                </div>
                <div className='address-editor-grid'>
                    <div className='form-group'>
                        <label htmlFor='address_city'>City</label>
                        <input
                            id='address_city'
                            type='text'
                            value={addressEditorState.city}
                            onChange={(event) => handleAddressEditorChange('city', event.target.value)}
                        />
                    </div>
                    <div className='form-group'>
                        <label htmlFor='address_state'>State</label>
                        <select
                            id='address_state'
                            value={addressEditorState.state}
                            onChange={(event) => handleAddressEditorChange('state', event.target.value)}
                        >
                            <option value=''>Select</option>
                            {STATE_OPTIONS.map((stateCode) => (
                                <option key={stateCode} value={stateCode}>{stateCode}</option>
                            ))}
                        </select>
                    </div>
                    <div className='form-group'>
                        <label htmlFor='address_zip'>Zip</label>
                        <input
                            id='address_zip'
                            type='text'
                            value={addressEditorState.zip}
                            onChange={(event) => handleAddressEditorChange('zip', event.target.value)}
                        />
                    </div>
                </div>
                <div className='user-reset-confirm-actions'>
                    <button type='button' className='btn btn-cancel' onClick={handleAddressEditorCancel}>Cancel</button>
                    <button
                        type='button'
                        className='btn'
                        onClick={handleAddressEditorSave}
                        disabled={!isAddressEditorValid()}
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
      )}
    </div>
  )

}

export default ModalUserEdit


